import type { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';

import { buildPackArchitectureGlobalFiltersSummary } from 'components/pages/PackArchitecture/packArchitectureGlobalFilters.helpers';

export const WHITESPACE_CELL_HOVER_TRACK_MIN_MS = 3000;
const VALID_ENTRY_POINTS = ['ppa_home', 'ppa_navigation', 'direct'] as const;
type EntryPoint = (typeof VALID_ENTRY_POINTS)[number];

export const buildWhitespaceCellId = (xAxisValue: string, yAxis: string, yAxisBucket: string) => `${xAxisValue || '-'}_${yAxis}_${yAxisBucket || '-'}`;
export const getWhitespaceToggleState = (showWhitespaces: boolean) => (showWhitespaces ? 'ON' : 'OFF');
export const getWhitespaceScrollDepthPct = (loaded: number, total: number) => total > 0 ? Math.round((loaded / total) * 100) : 100;
export const getWhitespaceExportDataScope = (scope: string) => scope === 'all_filtered_cells' ? 'all' : 'current';

export const getWhitespaceEntryPoint = (): EntryPoint => {
    const referrer = document.referrer || '';
    if (referrer.includes('/ppa/home')) return 'ppa_home';
    if (referrer.includes('/ppa/')) return 'ppa_navigation';
    const stored = localStorage.getItem('from');
    return VALID_ENTRY_POINTS.includes(stored as EntryPoint) ? (stored as EntryPoint) : 'direct';
};

export const computeWhitespaceGridMetrics = (gridRows: WhitespaceGridRow[], columnHeaders: string[]) => {
    let visibleCellCount = 0;
    let opportunityCellCount = 0;

    gridRows.forEach((row) => {
        columnHeaders.forEach((columnHeader) => {
            const cell = row.cells[columnHeader];
            if (!cell) return;
            visibleCellCount += 1;
            if (cell.isWhitespace) {
                opportunityCellCount += 1;
            }
        });
    });

    return {
        total_cell_count: gridRows.length * columnHeaders.length,
        visible_cell_count: visibleCellCount,
        opportunity_cell_count: opportunityCellCount,
    };
};

export const buildWhitespaceGlobalFiltersApplied = ({
    timeSelectionLabel,
    timePeriodList,
    allCheckedAttribute,
}: {
    timeSelectionLabel?: string;
    timePeriodList?: Array<{ timePeriod?: string; startTimestamp?: number; endTimestamp?: number }>;
    allCheckedAttribute?: Record<string, Record<string, unknown>>;
}) =>
    buildPackArchitectureGlobalFiltersSummary({
        timeSelectionLabel,
        timePeriodList,
        allCheckedAttribute,
    }) || '-';

export const getWhitespaceCellHoverProps = ({
    xAxisLabel,
    yAxisLabel,
    rowLabel,
    crossSection,
    hoverDurationMs,
}: {
    xAxisLabel: string;
    yAxisLabel: string;
    rowLabel: string;
    crossSection: string;
    hoverDurationMs?: number;
}) => ({
    x_axis_value: xAxisLabel,
    y_axis_value: yAxisLabel,
    axis_bucket_value: rowLabel,
    cross_section: crossSection,
    ...(hoverDurationMs !== undefined ? { hover_duration: hoverDurationMs } : {}),
});

export const estimateWhitespaceExportFileSizeMb = (payload: unknown) => {
    const bytes = new Blob([typeof payload === 'string' ? payload : JSON.stringify(payload)]).size;
    return Number((bytes / (1024 * 1024)).toFixed(4));
};
